import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Form } from 'react-bootstrap';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useDebounce } from 'use-debounce';
import { checkTeamName } from 'api/checkApi';
import { toast } from 'react-toastify';

const Step2 = ({
  teamName,
  setTeamName,
  teamDescription,
  setTeamDescription,
  setShouldContinue
}) => {
  const [isCheckingTeamName, setIsCheckingTeamName] = useState(false);
  const [teamNameNotAvaiable, setTeamNameNotAvaiable] = useState(null);

  const formik = useFormik({
    initialValues: {
      name: teamName,
      description: teamDescription
    },
    validationSchema: Yup.object().shape({
      name: Yup.string()
        .min(4, 'Il nome deve contenere almeno 4 caratteri.')
        .max(30, 'Il nome deve contenere al massimo 30 caratteri.')
        .trim()
        .required('Il nome della squadra è obbligatorio.'),
      description: Yup.string()
        .max(150, 'La descrizione deve contenere al massimo 150 caratteri.')
        .trim()
    }),
    validateOnMount: true
  });

  const [debouncedTeamName] = useDebounce(formik.values.name, 800);

  useEffect(() => {
    if (!formik.isValid) {
      setTeamNameNotAvaiable(null);
      setIsCheckingTeamName(false);
      return;
    }
    const checkTeamNameAvaiability = async () => {
      setIsCheckingTeamName(true);
      try {
        const response = await checkTeamName(debouncedTeamName);
        if (response.status === 200) {
          setTeamNameNotAvaiable(null);
        } else if (response.status === 422) {
          setTeamNameNotAvaiable(
            `La squadra ${debouncedTeamName} esiste già. Scegli un altro nome.`
          );
        }
      } catch (error) {
        toast.error(
          'Errore di rete durante il controllo della disponibilità del nome utente'
        );
      } finally {
        setIsCheckingTeamName(false);
      }
    };

    if (debouncedTeamName != '') checkTeamNameAvaiability();
  }, [debouncedTeamName]);

  useEffect(() => {
    if (formik.isValid && !isCheckingTeamName && !teamNameNotAvaiable) {
      setShouldContinue(true);
    } else {
      setShouldContinue(false);
    }
  }, [formik.isValid, isCheckingTeamName, teamNameNotAvaiable]);

  return (
    <>
      <Form.Group className="mb-3">
        <Form.Label>Nome della squadra</Form.Label>
        <Form.Control
          name="name"
          type="text"
          value={formik.values.name}
          placeholder="I Fantastici 5"
          onChange={e => {
            setIsCheckingTeamName(true);
            setTeamName(e.target.value);
            formik.handleChange(e);
          }}
          onBlur={formik.handleBlur}
          isValid={
            !isCheckingTeamName &&
            formik.touched.name &&
            !formik.errors.name &&
            !teamNameNotAvaiable
          }
          isInvalid={
            !isCheckingTeamName &&
            formik.touched.name &&
            (formik.errors.name || teamNameNotAvaiable)
          }
        />
        <Form.Control.Feedback>
          Il nome della squadra è disponibile.
        </Form.Control.Feedback>
        <Form.Control.Feedback type="invalid">
          {formik.errors.name || teamNameNotAvaiable}
        </Form.Control.Feedback>
      </Form.Group>

      <Form.Group>
        <Form.Label>Descrizione della squadra</Form.Label>
        <Form.Control
          name="description"
          type="text"
          value={formik.values.description}
          onChange={e => {
            setTeamDescription(e.target.value);
            formik.handleChange(e);
          }}
          onBlur={formik.handleBlur}
          placeholder="La squadra più forte del FantaClasse"
          isValid={formik.touched.description && !formik.errors.description}
          isInvalid={formik.touched.description && formik.errors.description}
        />
        <Form.Control.Feedback type="invalid">
          {formik.errors.description}
        </Form.Control.Feedback>
      </Form.Group>
    </>
  );
};

Step2.propTypes = {
  teamName: PropTypes.string.isRequired,
  setTeamName: PropTypes.func.isRequired,
  teamDescription: PropTypes.string.isRequired,
  setTeamDescription: PropTypes.func.isRequired,
  setShouldContinue: PropTypes.func.isRequired
};

export default Step2;
